import { Calibration, Point } from './types'

export function segmentsLength(points: Point[]): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y)
  }
  return total
}

/** Point at a given fraction (0..1) of the polyline's total length. */
export function pointAtFraction(points: Point[], fraction: number): Point {
  if (points.length === 0) return { x: 0, y: 0 }
  if (points.length === 1) return points[0]
  const total = segmentsLength(points)
  if (total === 0) return points[0]
  let target = total * Math.max(0, Math.min(1, fraction))
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x
    const dy = points[i].y - points[i - 1].y
    const d = Math.hypot(dx, dy)
    if (target <= d || i === points.length - 1) {
      const t = d === 0 ? 0 : target / d
      return { x: points[i - 1].x + dx * t, y: points[i - 1].y + dy * t }
    }
    target -= d
  }
  return points[points.length - 1]
}

/** Interior angle (degrees) at vertex b formed by segments a-b and b-c. */
export function interiorAngleDeg(a: Point, b: Point, c: Point): number {
  const v1 = { x: a.x - b.x, y: a.y - b.y }
  const v2 = { x: c.x - b.x, y: c.y - b.y }
  const m1 = Math.hypot(v1.x, v1.y)
  const m2 = Math.hypot(v2.x, v2.y)
  if (m1 === 0 || m2 === 0) return 0
  const cos = Math.max(-1, Math.min(1, (v1.x * v2.x + v1.y * v2.y) / (m1 * m2)))
  return (Math.acos(cos) * 180) / Math.PI
}

export function formatLength(worldPixels: number, calibration: Calibration | null): string {
  if (calibration && calibration.pixelsPerMeter > 0) {
    const m = worldPixels / calibration.pixelsPerMeter
    return `${m < 10 ? m.toFixed(2) : m.toFixed(1)} m`
  }
  return `${Math.round(worldPixels)} px`
}

export function formatAngle(deg: number): string {
  return `${Math.round(deg)}\u00b0`
}

/**
 * Choose a "nice" round metre value whose on-screen length sits in a readable
 * range, for drawing a scale bar. Returns the metre value and its pixel width.
 */
export function niceScaleBar(calibration: Calibration, zoom: number, targetPx = 110): { meters: number; px: number } {
  const pxPerMeterOnScreen = calibration.pixelsPerMeter * zoom
  const rawMeters = targetPx / pxPerMeterOnScreen
  const pow = Math.pow(10, Math.floor(Math.log10(rawMeters)))
  const candidates = [1, 2, 5, 10].map((m) => m * pow)
  let best = candidates[0]
  for (const candidate of candidates) {
    if (Math.abs(candidate * pxPerMeterOnScreen - targetPx) < Math.abs(best * pxPerMeterOnScreen - targetPx)) {
      best = candidate
    }
  }
  return { meters: best, px: best * pxPerMeterOnScreen }
}
